<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.plus-circle-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Create standard repair invoice') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app" to="/workshop">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <div class="p-5">
          <div class="px-4 py-8">
            <div class="flex items-center">
              <!-- step 1 -->
              <div :class="stepStage > 0 ? 'text-white' : 'text-gray-300'" class="flex items-center relative">
                <div
                  :class="stepStage > 0 ? 'bg-green-600 border-green-600' : 'text-gray-300 border-gray-300'"
                  class="rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 text-center"
                >
                  1
                </div>
                <div
                  :class="stepStage > 0 ? 'text-green-600' : 'text-gray-300'"
                  class="absolute top-0 -ml-10 text-center mt-16 w-32 text-xs font-medium uppercase"
                >
                  {{ $t('Repair Device') }}
                </div>
              </div>
              <!-- step 2 -->
              <div
                :class="stepStage > 1 ? 'border-green-600' : 'border-gray-300'"
                class="flex-auto border-t-2 transition duration-500 ease-in-out"
              ></div>
              <div :class="stepStage > 1 ? 'text-white' : 'text-gray-300'" class="flex items-center relative">
                <div
                  :class="stepStage > 1 ? 'bg-green-600 border-green-600' : 'text-gray-300 border-gray-300'"
                  class="rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 text-center"
                >
                  2
                </div>
                <div
                  :class="stepStage > 1 ? 'text-green-600' : 'text-gray-300'"
                  class="absolute top-0 -ml-10 text-center mt-16 w-32 text-xs font-medium uppercase"
                >
                  {{ $t('Priority level') }}
                </div>
              </div>
              <!-- step 3 -->
              <div
                :class="stepStage > 2 ? 'border-green-600' : 'border-gray-300'"
                class="flex-auto border-t-2 transition duration-500 ease-in-out"
              ></div>
              <div :class="stepStage > 2 ? 'text-white' : 'text-gray-300'" class="flex items-center relative">
                <div
                  :class="stepStage > 2 ? 'bg-green-600 border-green-600' : 'text-gray-300 border-gray-300'"
                  class="rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 text-center"
                >
                  3
                </div>
                <div
                  :class="stepStage > 2 ? 'text-green-600' : 'text-gray-300'"
                  class="absolute top-0 -ml-10 text-center mt-16 w-32 text-xs font-medium uppercase"
                >
                  {{ $t('Information & payment') }}
                </div>
              </div>
            </div>
          </div>
          <div class="mt-8 p-2">
            <loading :status="loading"></loading>
            <form @submit.prevent="submitRepair">
              <!-- content for step one -->
              <div :class="stepStage == 1 ? '' : 'hidden'" class="grid grid-cols-3">
                <!-- brand list start -->
                <template v-if="brandList.length">
                  <div class="col-span-3 flex flex-wrap -mx-1 overflow-hidden border-b pb-5">
                    <div class="py-2 px-1 w-full overflow-hidden md:w-1/4">
                      <h3 class="form-description-head">
                        {{ $t('Select your device brand') }}
                      </h3>
                      <p class="form-description-text">{{ $t('Brands we repair') }}.</p>
                    </div>
                    <div class="my-1 px-1 w-full overflow-hidden lg:w-3/4">
                      <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                        <template v-for="(brand, index) in brandList">
                          <div
                            :key="index"
                            @click="getDevices(brand)"
                            class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                          >
                            <div>
                              <div class="flex items-center justify-center">
                                <svg-vue
                                  class="w-5 h-5 text-green-400"
                                  icon="font-awesome.check-circle-solid"
                                  v-if="repair.brand_id == brand.id"
                                ></svg-vue>
                                <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                              </div>
                            </div>
                            <div class="flex flex-col ml-6">
                              <p class="text-sm leading-5">{{ brand.name }}</p>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- brand list end -->
                <!-- device list end -->
                <template v-if="deviceList.length">
                  <div class="col-span-3 flex flex-wrap -mx-1 overflow-hidden border-b">
                    <div class="py-2 px-1 w-full overflow-hidden md:w-1/4">
                      <h3 class="form-description-head">
                        {{ $t('Select your device') }}
                      </h3>
                      <p class="form-description-text">{{ $t('Available devices for selected brand') }}.</p>
                    </div>
                    <div class="my-1 px-1 w-full overflow-hidden lg:w-3/4">
                      <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                        <template v-for="(device, index) in deviceList">
                          <div
                            :key="index"
                            @click="getDefects(device)"
                            class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                          >
                            <div>
                              <div class="flex items-center justify-center">
                                <svg-vue
                                  class="w-5 h-5 text-green-400"
                                  icon="font-awesome.check-circle-solid"
                                  v-if="repair.device_id == device.id"
                                ></svg-vue>
                                <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                              </div>
                            </div>
                            <div class="flex flex-col ml-6">
                              <p class="text-sm leading-5">{{ device.name }}</p>
                              <p class="text-xs leading-4 group-focus:underline transition ease-in-out duration-150">
                                {{ device.model }}
                              </p>
                            </div>
                            <div class="ml-auto">
                              <img :alt="device.name" :src="device.avatar" class="inline-block rounded-full h-8 w-8" />
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- device list end -->
                <!-- defects list start -->
                <template v-if="defectList.length">
                  <div class="col-span-3 flex flex-wrap -mx-1 overflow-hidden py-2">
                    <div class="py-2 px-1 w-full overflow-hidden md:w-1/4">
                      <h3 class="form-description-head">
                        {{ $t('Select repairable issues') }}
                      </h3>
                      <p class="form-description-text">{{ $t('Available services/defect for selected device') }}.</p>
                    </div>
                    <div class="my-1 px-1 w-full overflow-hidden lg:w-3/4">
                      <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                        <template v-for="(defect, index) in defectList">
                          <div
                            :key="index"
                            @click="selectDefect(defect)"
                            class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                          >
                            <div>
                              <div class="flex items-center justify-center">
                                <svg-vue
                                  class="w-5 h-5 text-green-400"
                                  icon="font-awesome.check-circle-solid"
                                  v-if="currentDefects.includes(defect)"
                                ></svg-vue>
                                <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                              </div>
                            </div>
                            <div class="flex flex-col ml-6">
                              <p class="text-sm leading-5">
                                {{ defect.title }}
                              </p>
                              <p class="text-xs leading-4 group-focus:underline transition ease-in-out duration-150">
                                {{ $t('Required Time') }} :
                                {{ defect.required_time }}
                              </p>
                            </div>
                            <div class="ml-auto">
                              <small>{{ defect.price | currency }}</small>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- defects list end -->
              </div>
              <!-- end -->
              <!-- content for step two -->
              <div :class="stepStage == 2 ? '' : 'hidden'">
                <!-- priority list start -->
                <template v-if="priorityList.length && currentDefects.length">
                  <div class="md:col-span-3">
                    <div class="py-3">
                      <div class="border-t border-gray-200"></div>
                    </div>
                  </div>
                  <div class="md:col-span-1">
                    <h3 class="form-description-head">
                      {{ $t('Select priority') }}
                    </h3>
                    <p class="form-description-text">{{ $t('Repair processing priority') }}.</p>
                  </div>
                  <div class="mt-5 md:mt-0 md:col-span-2">
                    <div class="grid grid-cols-3 gap-3">
                      <div class="col-span-3">
                        <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                          <template v-for="(priority, index) in priorityList">
                            <div
                              :key="index"
                              @click="selectPriority(priority)"
                              class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                            >
                              <div>
                                <div class="flex items-center justify-center">
                                  <svg-vue
                                    class="w-5 h-5 text-green-400"
                                    icon="font-awesome.check-circle-solid"
                                    v-if="repair.repair_priority_id === priority.id"
                                  ></svg-vue>
                                  <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                                </div>
                              </div>
                              <div class="flex flex-col ml-6">
                                <p class="text-sm leading-5">
                                  {{ priority.name }}
                                </p>
                              </div>
                              <div class="ml-auto">
                                <small>{{ priority.extra_charge | currency }}</small>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <!-- priority list end -->
              </div>
              <!-- end -->
              <!-- content for step three -->
              <div :class="stepStage == 3 ? '' : 'hidden'">
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="name">
                      {{ $t('Name') }}
                    </label>
                    <div class="pt-1">
                      <input id="name" :placeholder="$t('Name')" class="form-input" required type="text" v-model="repair.name" />
                    </div>
                  </div>
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="email">
                      {{ $t('Email address') }}
                    </label>
                    <div class="pt-1">
                      <input
                        id="email"
                        :placeholder="$t('Email address')"
                        class="form-input"
                        required
                        type="email"
                        v-model="repair.email"
                      />
                    </div>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="phone">
                      {{ $t('Phone') }}
                    </label>
                    <div class="pt-1">
                      <input :placeholder="$t('Phone')" class="form-input" type="tel" id="phone" v-model="repair.phone" />
                    </div>
                  </div>
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="serial_number">
                      {{ $t('Serial number') }}
                    </label>
                    <div class="pt-1">
                      <input id="serial_number" :placeholder="$t('Serial number')" class="form-input" v-model="repair.serial_number" />
                    </div>
                  </div>
                </div>

                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="address">
                      {{ $t('Home address') }}
                    </label>
                    <div class="pt-1">
                      <input id="address" :placeholder="$t('Home address')" class="form-input" v-model="repair.address" />
                    </div>
                  </div>
                </div>
                <div class="flex flex-col md:flex-row">
                  <div class="w-full mx-2 pb-3 flex-1 svelte-1l8159u">
                    <label class="form-label" for="diagnostics">
                      {{ $t('Diagnostics') }}
                    </label>
                    <div class="pt-1">
                      <input-wysiwyg id="diagnostics" v-model="repair.diagnostics"></input-wysiwyg>
                    </div>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="is_device_collected">{{ $t('Device collected status') }}</label>
                  <input-switch
                    :disabled-label="$t('The customer will be notified to dispatch device')"
                    :enabled-label="$t('The device has been just collected from customer')"
                    id="is_device_collected"
                    v-model="repair.is_device_collected"
                  ></input-switch>
                  <div class="form-description-text">
                    {{ $t('This option will control to notify customer to dispatch his device via email') }}.
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="has_warranty">{{ $t('Device warranty status') }}</label>
                  <input-switch
                    :disabled-label="$t('No')"
                    :enabled-label="$t('Yes')"
                    id="has_warranty"
                    v-model="repair.has_warranty"
                  ></input-switch>
                  <div class="form-description-text">{{ $t('This option will set device has warranty or not') }}.</div>
                </div>
                <template v-if="currentDefects.length">
                  <div class="text-right px-4 py-3 sm:px-6">
                    <strong>{{ $t('Subtotal') }}</strong>
                    : {{ subTotalAmount | currency }}
                    <br />
                    <strong>{{ $t('Priority Process Charges') }}</strong>
                    : {{ currentPriority.extra_charge | currency }}
                    <br />
                    <strong>{{ $t('Tax') }}</strong>
                    : {{ taxAmount | currency }}
                    <br />
                    <strong>{{ $t('Total Amount') }}</strong>
                    : {{ totalAmountWithTax | currency }}
                  </div>
                  <div class="text-right px-4 py-3 sm:px-6">
                    <span class="inline-flex">
                      <router-link class="btn btn-secondary mr-4" to="/workshop/welcome">{{ $t('Cancel') }}</router-link>
                      <button class="btn btn-app" type="submit">
                        {{ $t('Save') }}
                      </button>
                    </span>
                  </div>
                </template>
              </div>
              <!-- end -->
            </form>
            <div class="mt-8 bg-gray-200 rounded-md text-right px-4 py-3 sm:px-6">
              <div class="flex justify-between">
                <button :disabled="stepStage < 2" @click="stepStage--" class="btn btn-app" type="reset">
                  {{ $t('Previous') }}
                </button>
                <button @click="stepStage++" class="btn btn-app" type="reset" v-if="!checkBeforeNext && stepStage < 3">
                  {{ $t('Next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'new',
  metaInfo() {
    return {
      title: this.$i18n.t('Manually create repair order'),
      titleTemplate: `%s - ${this.$store.state.settings.name}`,
    };
  },
  data() {
    return {
      stepStage: 1,
      loading: false,
      brandList: [],
      deviceList: [],
      defectList: [],
      priorityList: [],
      currentBrand: null,
      currentDevice: null,
      currentDefects: [],
      currentPriority: {
        extra_charge: 0,
      },
      repair: {
        brand_id: null,
        device_id: null,
        has_warranty: false,
        is_device_collected: false,
        defects: [],
        serial_number: null,
        name: null,
        email: null,
        phone: null,
        address: null,
        diagnostics: null,
        repair_priority_id: 1,
        sub_total: 0,
        total_cost: 0,
        profit: 0,
        tax: 0,
        total_charges: 0,
        user_id: null,
      },
      tax: {
        tax_rate: 0,
        is_tax_fix: false,
        is_tax_included: false,
      },
    };
  },
  mounted() {
    this.getBrandAndPriorityList();
    this.repair.user_id = this.$store.state.user.id;
  },
  computed: {
    checkBeforeNext() {
      const form = this.repair;
      if (this.stepStage === 1) {
        return !form.brand_id || !form.device_id || this.currentDefects.length < 1;
      }
      return false;
    },
    repairCostAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.cost;
      });
      return amount;
    },
    subTotalAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.price;
      });
      return amount;
    },
    profit() {
      return Number(this.totalAmountWithTax - this.repairCostAmount - this.taxAmount);
    },
    subTotalWithPriority() {
      const amount = Number(this.subTotalAmount) + Number(this.currentPriority.extra_charge);
      return amount;
    },
    taxAmount() {
      const amount = this.subTotalWithPriority;
      const rate = Number(this.tax.tax_rate ?? this.tax.rate);
      if (this.tax.is_vat) {
        if (this.tax.is_tax_included) {
          const vatAmount = amount - amount / (1 + rate / 100);
          return vatAmount.toFixed(2);
        } else {
          const vatAmount = amount * (rate / 100);
          return vatAmount.toFixed(2);
        }
      } else {
        if (this.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
    totalAmountWithTax() {
      if (this.tax.is_tax_included) {
        return this.subTotalWithPriority;
      }
      return this.subTotalWithPriority + Number(this.taxAmount);
    },
  },
  methods: {
    selectPriority(priority) {
      this.currentPriority = priority;
      this.repair.repair_priority_id = priority.id;
    },
    selectDefect(defect) {
      if (this.currentDefects.includes(defect)) {
        for (let i = 0; i < this.currentDefects.length; i++) {
          if (this.currentDefects[`${i}`] === defect) {
            this.currentDefects.splice(i, 1);
          }
        }
      } else {
        this.currentDefects.push(defect);
      }
    },
    submitRepair() {
      this.loading = true;
      this.repair.sub_total = this.subTotalAmount;
      this.repair.tax = this.taxAmount;
      this.repair.total_charges = this.totalAmountWithTax;
      this.repair.profit = this.profit;
      this.repair.total_cost = this.repairCostAmount;
      this.repair.defects = this.currentDefects;
      this.repair.payment_gateway_key = 'biller';
      this.repair.payment_gateway_value = this.$store.state.user.name;
      this.repair.user_id = this.$store.state.user.id;

      axios
        .post(route('repair-orders.store'), this.repair)
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading = false;
          this.$router.push(`/print/repair-order/${response.data.order.uuid}`);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getBrandAndPriorityList() {
      this.loading = true;
      this.deviceList = [];
      this.defectList = [];
      axios
        .get(route('repair.brand.list'))
        .then((response) => {
          this.brandList = response.data.brands;
          this.priorityList = response.data.priorities;
          this.getTax();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getTax() {
      axios
        .get(route('settings.get.tax'))
        .then((response) => {
          this.tax = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDevices(brand) {
      this.loading = true;
      this.defectList = [];
      this.currentBrand = brand;
      this.repair.brand_id = brand.id;
      axios
        .post(route('repair.device.list'), { brand_id: this.repair.brand_id })
        .then((response) => {
          this.deviceList = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDefects(device) {
      this.loading = true;
      this.currentDevice = device;
      this.repair.device_id = device.id;
      axios
        .post(route('repair.defect.list'), {
          device_id: this.repair.device_id,
        })
        .then((response) => {
          this.defectList = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
